import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { FileText, Plus, Copy, Trash2, Star, Edit2, Eye } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProposalTemplates = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/proposal-templates`, { credentials: 'include' });
      if (response.ok) {
        setTemplates(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const duplicateTemplate = async (templateId, e) => {
    e.stopPropagation();
    try {
      const response = await fetch(`${BACKEND_URL}/api/proposal-templates/${templateId}/duplicate`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const newTemplate = await response.json();
        navigate(`/proposals/templates/${newTemplate.template_id}/edit`);
      }
    } catch (error) {
      console.error('Duplicate error:', error);
    }
  };

  const setDefault = async (templateId, e) => {
    e.stopPropagation();
    try {
      await fetch(`${BACKEND_URL}/api/proposal-templates/${templateId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_default: true })
      });
      fetchTemplates();
    } catch (error) {
      console.error('Set default error:', error);
    }
  };

  const deleteTemplate = async (templateId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/proposal-templates/${templateId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        fetchTemplates();
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  if (loading) {
    return (
      <AppLayout title="Templates">
        <div className="flex items-center justify-center h-64">
          <div className="text-[#A2FF00] text-xl">Loading...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Templates">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Proposal Templates
            </h1>
            <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Reusable templates for creating proposals</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/proposals')}
              className={`px-4 py-2 ${isDark ? 'bg-[#111] border-white/20 text-white hover:border-[#A2FF00]' : 'bg-white border-gray-300 text-gray-700 hover:border-green-500'} border rounded-sm transition-colors`}
            >
              Back to Proposals
            </button>
            <button
              onClick={() => navigate('/proposals/templates/new/edit')}
              className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-green-500 hover:bg-green-600'} text-black font-bold rounded-sm transition-colors`}
            >
              <Plus className="w-5 h-5" />
              Create Template
            </button>
          </div>
        </div>

        {/* Templates Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <div
              key={template.template_id}
              onClick={() => navigate(`/proposals/templates/${template.template_id}/edit`)}
              className={`${isDark ? 'bg-[#111] border-white/10 hover:border-[#A2FF00]' : 'bg-white border-gray-200 hover:border-green-500'} border rounded-sm overflow-hidden transition-colors cursor-pointer group`}
            >
              {/* Preview Header */}
              <div className={`h-32 ${isDark ? 'bg-gradient-to-br from-[#A2FF00]/20 to-[#0A0A0A]' : 'bg-gradient-to-br from-green-100 to-gray-50'} p-4 relative`}>
                <div className={`w-8 h-8 ${isDark ? 'bg-[#A2FF00]' : 'bg-green-500'} rounded-sm flex items-center justify-center`}>
                  <FileText className={`w-5 h-5 ${isDark ? 'text-black' : 'text-white'}`} />
                </div>
                {template.is_default && (
                  <span className={`absolute top-4 right-4 flex items-center gap-1 px-2 py-1 ${isDark ? 'bg-[#A2FF00]/20 text-[#A2FF00]' : 'bg-green-100 text-green-600'} text-xs rounded`}>
                    <Star className="w-3 h-3" />
                    Default
                  </span>
                )}
                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Edit2 className={`w-5 h-5 ${isDark ? 'text-[#A2FF00]' : 'text-green-500'}`} />
                </div>
              </div>
              
              {/* Content */}
              <div className="p-4">
                <h3 className={`${isDark ? 'text-white group-hover:text-[#A2FF00]' : 'text-gray-900 group-hover:text-green-600'} font-medium mb-1 transition-colors`}>
                  {template.name}
                </h3>
                {template.description && (
                  <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'} text-sm mb-3 line-clamp-2`}>{template.description}</p>
                )}
                <div className={`${isDark ? 'text-gray-500' : 'text-gray-400'} text-xs mb-4`}>
                  {template.sections?.length || 0} sections
                  {template.default_products?.length > 0 && (
                    <span className="ml-2">• {template.default_products.length} product{template.default_products.length > 1 ? 's' : ''}</span>
                  )}
                </div>
                
                {/* Actions */}
                <div className={`flex items-center gap-2 pt-3 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                  <button
                    onClick={(e) => duplicateTemplate(template.template_id, e)}
                    className={`flex items-center justify-center gap-1 px-3 py-2 ${isDark ? 'bg-[#A2FF00]/10 text-[#A2FF00] hover:bg-[#A2FF00]/20' : 'bg-green-50 text-green-600 hover:bg-green-100'} text-sm rounded-sm transition-colors`}
                  >
                    <Copy className="w-4 h-4" />
                    Copy
                  </button>
                  {!template.is_default && (
                    <button
                      onClick={(e) => setDefault(template.template_id, e)}
                      className={`p-2 ${isDark ? 'text-gray-400 hover:text-[#A2FF00] hover:bg-[#A2FF00]/10' : 'text-gray-400 hover:text-green-600 hover:bg-green-50'} rounded-sm transition-colors`}
                      title="Set as default"
                    >
                      <Star className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={(e) => deleteTemplate(template.template_id, e)}
                    className={`p-2 text-red-400 ${isDark ? 'hover:bg-red-500/20' : 'hover:bg-red-50'} rounded-sm transition-colors`}
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {templates.length === 0 && (
          <div className={`text-center py-12 ${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm`}>
            <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>No templates yet</h3>
            <p className={`mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Create your first proposal template to get started</p>
            <button
              onClick={() => navigate('/proposals/templates/new/edit')}
              className={`px-4 py-2 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-green-500 hover:bg-green-600'} text-black font-bold rounded-sm transition-colors`}
            >
              Create Template
            </button>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default ProposalTemplates;
